import { Avatar, Button, Empty, Modal, Skeleton, Spin, Typography } from "antd";
import {
  CheckCircleFilled,
  EditOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { message } from "@bindeleddet/core/utils";
import { CompanyContact } from "@bindeleddet/core/types";
import axios from "axios";
import CreateOrEditContact from "components/EditCompanyContacts/CompanyContactBox/CreateOrEditContact";
import React, { useEffect, useState } from "react";

const { Text } = Typography;

const boxStyle: React.CSSProperties = {
  border: "1px dashed var(--neutral-alter)",
  backgroundColor: "var(--neutral-alt)",
  borderRadius: "6px",
  padding: "12px 16px",
};

const initials = (contact: CompanyContact) =>
  `${contact.first_name.charAt(0)}${contact.last_name.charAt(0)}`.toUpperCase();

const ContactDetails: React.FC<{ contact: CompanyContact }> = ({ contact }) => (
  <div style={{ flex: "1 1 200px", minWidth: 0 }}>
    <Text strong>
      {contact.first_name} {contact.last_name}
    </Text>
    {contact.position && (
      <Text type="secondary" style={{ display: "block" }}>
        {contact.position}
      </Text>
    )}
    <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 16px", marginTop: "2px" }}>
      {contact.email ? (
        <Text>
          <MailOutlined style={{ marginRight: "6px" }} />
          {contact.email}
        </Text>
      ) : (
        <Text type="warning">
          <ExclamationCircleOutlined style={{ marginRight: "6px" }} />
          Mangler e-postadresse
        </Text>
      )}
      {contact.telephone && (
        <Text>
          <PhoneOutlined style={{ marginRight: "6px" }} />
          {contact.telephone}
        </Text>
      )}
    </div>
  </div>
);

// Which contact form is open: none, a new contact, or editing an existing contact
type FormState = { open: false } | { open: true; contact?: CompanyContact; key: number };

interface Props {
  companyId: number;
  companyName: string;
  onChange: (contact: CompanyContact | undefined) => void;
}

/**
 * Shows the contact person for the booking and lets the company pick, create, edit and delete contacts in pop-ups.
 * The selected contact is stored as the company's main contact, which the backend uses for the booking.
 */
const ContactSection: React.FC<Props> = ({ companyId, companyName, onChange }) => {
  const [contacts, setContacts] = useState<CompanyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [form, setForm] = useState<FormState>({ open: false });
  const [selecting, setSelecting] = useState<number | undefined>();

  const selected = contacts.find(contact => contact.main_contact);

  useEffect(() => {
    axios
      .get<CompanyContact[]>(`/companies/${companyId}/contact-persons/`)
      .then(response => setContacts(response.data))
      .catch(() => message("error", "Kunne ikke hente kontaktpersoner"))
      .finally(() => setLoading(false));
  }, [companyId]);

  // Only reported once the contacts are loaded, so the parent doesn't briefly think there is no contact person
  useEffect(() => {
    if (!loading) onChange(selected);
  }, [loading, selected, onChange]);

  const openForm = (contact?: CompanyContact) => setForm({ open: true, contact, key: Date.now() });

  const select = async (contact: CompanyContact) => {
    if (contact.main_contact) {
      setPickerOpen(false);
      return;
    }
    setSelecting(contact.id);
    try {
      // The backend unsets the previous main contact in the same transaction
      await axios.patch(`/companies/${companyId}/contact-persons/${contact.id}/`, { main_contact: true });
      setContacts(prev => prev.map(c => ({ ...c, main_contact: c.id === contact.id })));
      setPickerOpen(false);
    } catch {
      message("error", "Kunne ikke velge kontaktperson");
    } finally {
      setSelecting(undefined);
    }
  };

  const handleCreate = (contact: CompanyContact) => {
    setContacts(prev => [...prev, contact]);
    // A new contact is most likely meant for this booking
    select(contact);
  };

  const handleUpdate = (contact: CompanyContact) =>
    setContacts(prev => prev.map(c => (c.id === contact.id ? contact : c)));

  const handleDelete = (contact: CompanyContact) => {
    if (contact.main_contact) {
      message("error", "Velg en annen kontaktperson for bookingen før du sletter denne.");
      return;
    }
    axios
      .delete(`/companies/${companyId}/contact-persons/${contact.id}/`)
      .then(() => {
        message("success", "Kontaktperson slettet");
        setContacts(prev => prev.filter(c => c.id !== contact.id));
        setForm({ open: false });
      })
      .catch(() => message("error", "Kunne ikke slette kontaktperson"));
  };

  return (
    <>
      <div style={{ ...boxStyle, maxWidth: "520px" }}>
        {loading ? (
          <Skeleton avatar active paragraph={{ rows: 1 }} />
        ) : selected ? (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Avatar size={44} style={{ backgroundColor: "var(--primary-color)", flexShrink: 0 }}>
              {initials(selected)}
            </Avatar>
            <ContactDetails contact={selected} />
            <Button onClick={() => setPickerOpen(true)}>Endre</Button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Avatar size={44} icon={<UserOutlined />} style={{ flexShrink: 0 }} />
            <Text style={{ flex: "1 1 200px" }}>
              {companyName} har ikke valgt en kontaktperson enda. Velg en før dere booker, slik at vi kan nå dere når
              arrangementet nærmer seg.
            </Text>
            <Button type="primary" onClick={() => setPickerOpen(true)}>
              Velg kontaktperson
            </Button>
          </div>
        )}
      </div>
      <Button type="link" icon={<PlusOutlined />} onClick={() => openForm()} style={{ padding: 0, marginTop: "8px" }}>
        Opprett ny kontaktperson
      </Button>

      <Modal
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        footer={null}
        centered
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingRight: "32px" }}>
            <span>Velg kontaktperson</span>
            <Button size="small" icon={<PlusOutlined />} onClick={() => openForm()}>
              Opprett ny
            </Button>
          </div>
        }
      >
        <Text type="secondary" style={{ display: "block", marginBottom: "12px" }}>
          Trykk på en kontaktperson for å velge vedkommende som kontaktperson for arrangementet.
        </Text>
        {contacts.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Dere har ingen kontaktpersoner enda" />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {contacts.map(contact => (
              <div
                key={contact.id}
                role="button"
                tabIndex={0}
                onClick={() => select(contact)}
                onKeyDown={e => {
                  // Ignore key presses bubbling up from the edit button inside the row
                  if (e.target !== e.currentTarget) return;
                  if (e.key === "Enter" || e.key === " ") {
                    // Keep space from scrolling the modal
                    e.preventDefault();
                    select(contact);
                  }
                }}
                style={{
                  ...boxStyle,
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  cursor: "pointer",
                  borderStyle: contact.main_contact ? "solid" : "dashed",
                  borderColor: contact.main_contact ? "var(--primary-color)" : "var(--neutral-alter)",
                }}
              >
                <Button
                  type="text"
                  title="Rediger kontaktperson"
                  icon={<EditOutlined />}
                  onClick={e => {
                    e.stopPropagation();
                    openForm(contact);
                  }}
                />
                <ContactDetails contact={contact} />
                {selecting === contact.id ? (
                  <Spin size="small" />
                ) : (
                  contact.main_contact && (
                    <CheckCircleFilled style={{ color: "var(--primary-color)", fontSize: "18px" }} />
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {form.open && (
        <CreateOrEditContact
          key={form.key}
          isOpen
          onClose={() => setForm({ open: false })}
          companyId={companyId}
          contact={form.contact}
          handleCreate={handleCreate}
          handleUpdate={handleUpdate}
          handleDelete={handleDelete}
        />
      )}
    </>
  );
};

export default ContactSection;
